import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getIchimokuEntryConditions } from '../../api/tradeGroups';
import { useAutoRefreshTrigger } from '../../stores/useGlobalAutoRefreshStore';
import './IchimokuEntryConditionsBar.css';

/**
 * Props for IchimokuEntryConditionsBar.
 * Self-fetching per PLAN-044 (mirrors VwapEntryConditionsBar / PLAN-AMEND-008):
 * the Bar owns its own useQuery. TanStack dedupes the query against
 * IchimokuExitConditionsBar so a single fetch serves both surfaces.
 */
export interface IchimokuEntryConditionsBarProps {
  tradeGroupId: string;
  className?: string;
}

const STALE_TIME_MS = 30_000;

/**
 * Sub-$1 assets collapse at 2 decimals; mirror VwapEntryConditionsBar's
 * adaptive-precision rule (4 decimals < $1, 2 decimals ≥ $1).
 */
const formatPrice = (value: number | null): string => {
  if (value === null) return '—';
  if (Math.abs(value) < 1) {
    return `$${value.toFixed(4)}`;
  }
  return `$${value.toFixed(2)}`;
};

interface SegmentSpec {
  key: string;
  shortLabel: string;
  fullLabel: string;
  value: boolean | null;
}

/**
 * Returns CSS modifier and symbol for a segment given the raw condition value.
 * `null` = warmup (indicator not yet computable) → gray.
 */
const statusFor = (value: boolean | null): { cls: string; symbol: string } => {
  if (value === null) return { cls: 'warmup', symbol: '–' };
  if (value === true) return { cls: 'met', symbol: '✓' };
  return { cls: 'unmet', symbol: '✗' };
};

const IchimokuEntryConditionsBar: React.FC<IchimokuEntryConditionsBarProps> = ({
  tradeGroupId,
  className = '',
}) => {
  const refreshTrigger = useAutoRefreshTrigger('trade-groups');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['ichimoku-entry-conditions', tradeGroupId, refreshTrigger],
    queryFn: () => getIchimokuEntryConditions(tradeGroupId),
    staleTime: STALE_TIME_MS,
    retry: 1,
  });

  if (isError) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={`ichimoku-entry-conditions-bar loading ${className}`}>
        <div className="ichimoku-gauge" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const {
    current_price,
    cloud_top,
    cloud_bottom,
    entry_conditions,
  } = data;

  const segments: SegmentSpec[] = [
    { key: 'price_above_cloud',         shortLabel: 'P>C',  fullLabel: 'Price > Cloud',        value: entry_conditions?.price_above_cloud ?? null },
    { key: 'tk_aligned',                shortLabel: 'T>K',  fullLabel: 'Tenkan > Kijun',       value: entry_conditions?.tk_aligned ?? null },
    { key: 'chikou_above_price_lag26',  shortLabel: 'Chk',  fullLabel: 'Chikou > Price (−26)', value: entry_conditions?.chikou_above_price_lag26 ?? null },
    { key: 'cloud_bullish',             shortLabel: 'A>B',  fullLabel: 'Cloud Bullish (A>B)',  value: entry_conditions?.cloud_bullish ?? null },
    { key: 'tk_cross_above_cloud',      shortLabel: 'TKx',  fullLabel: 'TK Cross Above Cloud', value: entry_conditions?.tk_cross_above_cloud ?? null },
  ];

  const metCount = entry_conditions?.conditions_met_count ?? 0;

  return (
    <div className={`ichimoku-entry-conditions-bar ${className}`}>
      <div className="ichimoku-entry-condition-row">
        <span className="ichimoku-entry-condition-label">Entry Conditions:</span>
        <span
          className="ichimoku-conditions-count"
          role="status"
          aria-label={`${metCount} of 5 entry conditions met`}
        >
          {metCount} of 5
        </span>
      </div>

      <div
        className="ichimoku-gauge"
        role="img"
        aria-label="Ichimoku entry-conditions gauge — five segments, one per entry condition"
      >
        {segments.map(seg => {
          const { cls, symbol } = statusFor(seg.value);
          return (
            <div
              key={seg.key}
              className={`ichimoku-gauge-segment ${cls}`}
              title={`${seg.fullLabel}: ${seg.value === null ? 'warmup' : seg.value ? 'met' : 'unmet'}`}
              aria-label={`${seg.fullLabel}: ${seg.value === null ? 'warmup' : seg.value ? 'met' : 'unmet'}`}
            >
              <span className="ichimoku-gauge-segment-symbol">{symbol}</span>
              <span className="ichimoku-gauge-segment-label">{seg.shortLabel}</span>
            </div>
          );
        })}
      </div>

      <div className="ichimoku-numeric-row">
        <div className="ichimoku-numeric-cell">
          <span className="ichimoku-numeric-label">Price</span>
          <span className="ichimoku-numeric-value">{formatPrice(current_price)}</span>
        </div>
        <div className="ichimoku-numeric-cell">
          <span className="ichimoku-numeric-label">Cloud Top</span>
          <span className="ichimoku-numeric-value">{formatPrice(cloud_top)}</span>
        </div>
        <div className="ichimoku-numeric-cell">
          <span className="ichimoku-numeric-label">Cloud Bottom</span>
          <span className="ichimoku-numeric-value">{formatPrice(cloud_bottom)}</span>
        </div>
      </div>
    </div>
  );
};

export default IchimokuEntryConditionsBar;
